<template>
  <div class="endorsement-history-container">
    <!-- Date Filters (Optional UI overlay controlled via showFilters) -->
    <div v-if="showFilters" class="row mb-3 align-items-end">
      <div class="col-md-4">
        <label for="history-from-date" class="form-label font-weight-bold"
          >From Date</label
        >
        <input
          id="history-from-date"
          v-model="localFromDate"
          type="date"
          class="form-control form-control-sm"
        />
      </div>
      <div class="col-md-4">
        <label for="history-to-date" class="form-label font-weight-bold"
          >To Date</label
        >
        <input
          id="history-to-date"
          v-model="localToDate"
          type="date"
          class="form-control form-control-sm"
        />
      </div>
      <div class="col-md-4 d-flex align-items-end">
        <button
          type="button"
          class="btn btn-sm btn-secondary me-2"
          style="margin-right: 8px"
          @click="resetLocalFilters"
        >
          Reset Dates
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center my-3">
      <span>Loading endorsement history...</span>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="alert alert-danger" role="alert">
      {{ error }}
    </div>

    <!-- Export Toolbar Component -->
    <TableExportButtons
      v-if="formattedHistoryList.length > 0"
      :columns="columnConfig"
      :data="formattedHistoryList"
      file-name="endorsement_history"
      title="Certification Endorsement History"
    />

    <!-- Empty State -->
    <div v-else-if="formattedHistoryList.length === 0" class="text-muted my-2">
      No endorsement history recorded.
    </div>

    <!-- Data Table -->
    <table
      v-if="formattedHistoryList.length > 0"
      class="table table-striped table-bordered table-sm mt-2"
    >
      <thead>
        <tr>
          <th v-for="col in columnConfig" :key="col.key">{{ col.label }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="item in formattedHistoryList" :key="item.id">
          <td>{{ item.vessel_name || "-" }}</td>
          <td>{{ item.vessel_type_text }}</td>
          <td>{{ item.imo_number || "-" }}</td>
          <td>{{ item.official_number || "-" }}</td>
          <td>{{ item.certificate || "-" }}</td>
          <td>{{ formatDate(item.endorsement_date) }}</td>
          <td>{{ item.endorsed_by_name || "-" }}</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import { getEndorsementHistory, getVesselTypes } from "@/apis/vessels";
import TableExportButtons from "@/components/tableExport/TableExportButtons.vue";

export default {
  name: "CertificationEndorsementHistory",
  components: { TableExportButtons },
  props: {
    // 1. Bundle Object Filter Option
    filters: {
      type: Object,
      default: () => ({}),
    },
    // 2. Individual Prop Options (All optional)
    certificateId: {
      type: [Number, String],
      default: null,
    },
    vesselId: {
      type: [Number, String],
      default: null,
    },
    vesselType: {
      type: [Number, String],
      default: null,
    },
    certificate: {
      type: String,
      default: null,
    },
    endorsedBy: {
      type: [Number, String],
      default: null,
    },
    userId: {
      // Alias for endorsedBy
      type: [Number, String],
      default: null,
    },
    from: {
      type: String,
      default: null,
    },
    to: {
      type: String,
      default: null,
    },
    showFilters: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      rawHistoryList: [],
      loading: false,
      error: null,
      vesselTypes: [],
      localFromDate: null,
      localToDate: null,

      columnConfig: [
        { label: "Vessel Name", key: "vessel_name" },
        { label: "Vessel Type", key: "vessel_type_text" },
        { label: "IMO Number", key: "imo_number" },
        { label: "Official Number", key: "official_number" },
        { label: "Certificate", key: "certificate" },
        {
          label: "Endorsement Date",
          key: "endorsement_date",
          formatter: (val) => this.formatDate(val),
        },
        { label: "Endorsed By", key: "endorsed_by_name" },
      ],
    };
  },
  computed: {
    // Merges object prop, individual props, and local date inputs seamlessly
    apiParams() {
      const merged = {
        certificate_id:
          this.certificateId ?? this.filters.certificate_id ?? null,
        vessel_id: this.vesselId ?? this.filters.vessel_id ?? null,
        vessel_type: this.vesselType ?? this.filters.vessel_type ?? null,
        certificate: this.certificate ?? this.filters.certificate ?? null,
        endorsed_by:
          this.endorsedBy ??
          this.userId ??
          this.filters.endorsed_by ??
          this.filters.userId ??
          null,
        from: this.localFromDate || this.from || this.filters.from || null,
        to: this.localToDate || this.to || this.filters.to || null,
      };

      // Strip out empty or null keys before sending to API
      return Object.fromEntries(
        Object.entries(merged).filter(
          ([, val]) => val !== null && val !== undefined && val !== ""
        )
      );
    },

    // Vue reactive transform for vessel type names
    formattedHistoryList() {
      if (!this.rawHistoryList.length) return [];

      const typesMap = new Map(this.vesselTypes.map((vt) => [vt.id, vt.type]));

      return this.rawHistoryList.map((item) => ({
        ...item,
        vessel_type_text: typesMap.get(item.vessel_type) || "-",
      }));
    },
  },
  watch: {
    // Re-fetch automatically whenever any parameter changes
    apiParams: {
      deep: true,
      immediate: true,
      handler() {
        this.fetchHistory();
      },
    },
  },
  methods: {
    async fetchVesselTypes() {
      if (this.vesselTypes.length > 0) return;
      try {
        const response = await getVesselTypes();
        this.vesselTypes = response.data || [];
      } catch (err) {
        console.error("Failed to fetch vessel types:", err);
      }
    },
    async fetchHistory() {
      // Don't call API if no parameters are present
      if (Object.keys(this.apiParams).length === 0) {
        this.rawHistoryList = [];
        return;
      }

      this.loading = true;
      this.error = null;

      try {
        const response = await getEndorsementHistory(this.apiParams);
        this.rawHistoryList = response.data || [];
      } catch (err) {
        console.error("Failed to fetch endorsement history:", err);
        this.error = "Failed to load endorsement history.";
      } finally {
        this.loading = false;
      }
    },
    resetLocalFilters() {
      this.localFromDate = null;
      this.localToDate = null;
    },
    formatDate(dateVal) {
      if (!dateVal) return "-";
      const date = new Date(dateVal);
      if (isNaN(date.getTime())) return dateVal;

      // Extract day, month, and year with zero padding
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();

      return `${day}-${month}-${year}`;
    },
  },
  mounted() {
    this.fetchVesselTypes();
  },
};
</script>
